import React, { useState, useEffect } from "react";
import { useParams, useHistory } from "react-router-dom";

import { toast } from "react-toastify";
import openSocket from "../../services/socket-io";
import clsx from "clsx";

import { Paper, makeStyles, Button } from "@material-ui/core";
import AssignmentOutlinedIcon from "@material-ui/icons/AssignmentOutlined";
import HeadsetMicOutlinedIcon from "@material-ui/icons/HeadsetMicOutlined";
import ChamadoInternoDialog from "../../suporte/ChamadoInternoDialog";
import { usePermissao } from "../../suporte/permissoes";

import ContactDrawer from "../ContactDrawer";
import MessageInput from "../MessageInput/";
import TicketHeader from "../TicketHeader";
import TicketInfo from "../TicketInfo";
import TicketActionButtons from "../TicketActionButtons";
import MessagesList from "../MessagesList";
import api from "../../services/api";
import { ReplyMessageProvider } from "../../context/ReplyingMessage/ReplyingMessageContext";
import toastError from "../../errors/toastError";
import CardDrawer, { EVENTO_CARD } from "../../suporte/CardDrawer";

const drawerWidth = 320;

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    height: "100%",
    position: "relative",
    overflow: "hidden",
  },

  ticketInfo: {
    maxWidth: "50%",
    flexBasis: "50%",
    [theme.breakpoints.down("sm")]: {
      maxWidth: "80%",
      flexBasis: "80%",
    },
  },
  ticketActionButtons: {
    maxWidth: "50%",
    flexBasis: "50%",
    display: "flex",
    [theme.breakpoints.down("sm")]: {
      maxWidth: "100%",
      flexBasis: "100%",
      marginBottom: "5px",
    },
  },

  mainWrapper: {
    flex: 1,
    height: "100%",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
    borderLeft: "0",
    marginRight: -drawerWidth,
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },

  mainWrapperShift: {
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginRight: 0,
  },
}));

const Ticket = () => {
  const podeAbrirCard = usePermissao("cards.abrir");
  const { ticketId } = useParams();
  const history = useHistory();
  const classes = useStyles();

  // gaveta lateral: "contato" (original do Whaticket) ou "card" (chamado do suporte)
  const [painel, setPainel] = useState(null);
  const drawerOpen = painel !== null;
  const [pedidoCard, setPedidoCard] = useState(0); // clique no botão Card: abre o card se ainda não houver
  const [interno, setInterno] = useState(false); // janela do chamado interno
  const [loading, setLoading] = useState(true);
  const [contact, setContact] = useState({});
  const [ticket, setTicket] = useState({});

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchTicket = async () => {
        try {
          const { data } = await api.get("/tickets/" + ticketId);

          setContact(data.contact);
          setTicket(data);
          setLoading(false);
        } catch (err) {
          setLoading(false);
          toastError(err);
        }
      };
      fetchTicket();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [ticketId, history]);

  useEffect(() => {
    const socket = openSocket();

    socket.on("connect", () => socket.emit("joinChatBox", ticketId));

    socket.on("ticket", (data) => {
      if (data.action === "update") {
        setTicket(data.ticket);
      }

      if (data.action === "delete") {
        toast.success("Ticket deleted sucessfully.");
        history.push("/tickets");
      }
    });

    socket.on("contact", (data) => {
      if (data.action === "update") {
        setContact((prevState) => {
          if (prevState.id === data.contact?.id) {
            return { ...prevState, ...data.contact };
          }
          return prevState;
        });
      }
    });

    return () => {
      socket.disconnect();
    };
  }, [ticketId, history]);

  const handleDrawerOpen = () => {
    setPainel("contato");
  };

  const handleDrawerClose = () => {
    setPainel(null);
  };

  // abre a gaveta do card quando ele é criado (ao aceitar ou pelo menu da mensagem)
  useEffect(() => {
    const ouvir = (e) => {
      if (e.detail?.ticketId === String(ticketId)) setPainel("card");
    };
    window.addEventListener(EVENTO_CARD, ouvir);
    return () => window.removeEventListener(EVENTO_CARD, ouvir);
  }, [ticketId]);

  return (
    <div className={classes.root} id="drawer-container">
      <Paper
        variant="outlined"
        elevation={0}
        className={clsx(classes.mainWrapper, {
          [classes.mainWrapperShift]: drawerOpen,
        })}
      >
        <TicketHeader loading={loading}>
          <div className={classes.ticketInfo}>
            <TicketInfo
              contact={contact}
              ticket={ticket}
              onClick={handleDrawerOpen}
            />
          </div>
          <div className={classes.ticketActionButtons}>
            <div style={{ display: "flex", gap: 6, alignSelf: "center", marginLeft: "auto", marginRight: 6 }}>
              {podeAbrirCard && (
              <>
              <Button
                size="small"
                variant={painel === "card" ? "contained" : "outlined"}
                color="primary"
                startIcon={<AssignmentOutlinedIcon />}
                onClick={() => {
                  if (painel === "card") return setPainel(null);
                  setPainel("card");
                  setPedidoCard(Date.now());
                }}
              >
                Chamado técnico
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<HeadsetMicOutlinedIcon />}
                onClick={() => setInterno(true)}
              >
                Chamado interno
              </Button>
              </>
            )}
            </div>
            <TicketActionButtons ticket={ticket} />
          </div>
        </TicketHeader>
        <ReplyMessageProvider>
          <MessagesList
            ticketId={ticketId}
            isGroup={ticket.isGroup}
          ></MessagesList>
          <MessageInput ticketStatus={ticket.status} queueId={ticket.queueId} />
        </ReplyMessageProvider>
      </Paper>
      <ChamadoInternoDialog
        open={interno}
        onClose={() => setInterno(false)}
        ticketId={ticketId}
        nomeContato={contact?.name}
        onCriado={() => {
          setPainel("card");
          window.dispatchEvent(new CustomEvent(EVENTO_CARD, { detail: { ticketId: String(ticketId) } }));
        }}
      />
      <CardDrawer
        open={painel === "card"}
        onClose={handleDrawerClose}
        ticketId={ticketId}
        pedidoAbrir={pedidoCard}
      />
      <ContactDrawer
        open={painel === "contato"}
        handleDrawerClose={handleDrawerClose}
        contact={contact}
        loading={loading}
      />
    </div>
  );
};

export default Ticket;
